import { lazy, ComponentType } from 'react';
import { getComponentsForRouteFromRegistry } from './routeComponentsRegistry';

// Lazy load components for better performance
const TeamActivityFeed = lazy(() => import('@/components/dashboard/TeamActivityFeed'));
const TasksAssignedGrid = lazy(() => import('@/components/dashboard/TasksAssignedGrid'));
const TeamAvatars = lazy(() => import('@/components/dashboard/TeamAvatars'));
const TeamOnVacation = lazy(() => import('@/components/dashboard/TeamOnVacation'));
const ActiveBrands = lazy(() => import('@/components/dashboard/ActiveBrands'));
const TasksDueToday = lazy(() => import('@/components/dashboard/TasksDueToday'));
const TasksCompletedToday = lazy(() => import('@/components/dashboard/TasksCompletedToday'));
const ProjectProgress = lazy(() => import('@/components/dashboard/ProjectProgress'));
const ProjectProgressScore = lazy(() => import('@/components/dashboard/ProjectProgressScore'));
const LeadsSummary = lazy(() => import('@/components/dashboard/LeadsSummary'));
const LeadsDailyTrends = lazy(() => import('@/components/dashboard/leads/LeadsDailyTrends'));
const LeadsChannelsChart = lazy(() => import('@/components/dashboard/leads/LeadsChannelsChart'));
const LeadsMediumChart = lazy(() => import('@/components/dashboard/leads/LeadsMediumChart'));
const LeadsSourcesChart = lazy(() => import('@/components/dashboard/leads/LeadsSourcesChart'));
const LeadsChannelsByProductChart = lazy(() => import('@/components/dashboard/leads/LeadsChannelsByProductChart'));
const LeadsMediumByProductChart = lazy(() => import('@/components/dashboard/leads/LeadsMediumByProductChart'));
const WeeklyCalendarView = lazy(() => import('@/components/dashboard/WeeklyCalendarView').then(m => ({ default: m.WeeklyCalendarView })));
const VisualsToBeApproved = lazy(() => import('@/components/dashboard/VisualsToBeApproved').then(m => ({ default: m.VisualsToBeApproved })));
const UpcomingAppointments = lazy(() => import('@/components/dashboard/UpcomingAppointments').then(m => ({ default: m.UpcomingAppointments })));
const FteTasksList = lazy(() => import('@/components/dashboard/FteTasksList'));
const ConversionFunnel = lazy(() => import('@/components/dashboard/ConversionFunnel'));
const ConversionFunnelToday = lazy(() => import('@/components/dashboard/DashboardWidgetWrappers').then(m => ({ default: m.ConversionFunnelToday })));
const ConversionFunnelLast7Days = lazy(() => import('@/components/dashboard/DashboardWidgetWrappers').then(m => ({ default: m.ConversionFunnelLast7Days })));
const ConversionFunnelLast14Days = lazy(() => import('@/components/dashboard/DashboardWidgetWrappers').then(m => ({ default: m.ConversionFunnelLast14Days })));
const ConversionFunnelLastMonth = lazy(() => import('@/components/dashboard/DashboardWidgetWrappers').then(m => ({ default: m.ConversionFunnelLastMonth })));
const ConversionFunnelThisMonth = lazy(() => import('@/components/dashboard/DashboardWidgetWrappers').then(m => ({ default: m.ConversionFunnelThisMonth })));
const LeadsSummaryToday = lazy(() => import('@/components/dashboard/DashboardWidgetWrappers').then(m => ({ default: m.LeadsSummaryToday })));
const LeadsSummaryLast7Days = lazy(() => import('@/components/dashboard/DashboardWidgetWrappers').then(m => ({ default: m.LeadsSummaryLast7Days })));
const LeadsSummaryLast14Days = lazy(() => import('@/components/dashboard/DashboardWidgetWrappers').then(m => ({ default: m.LeadsSummaryLast14Days })));
const LeadsSummaryLastMonth = lazy(() => import('@/components/dashboard/DashboardWidgetWrappers').then(m => ({ default: m.LeadsSummaryLastMonth })));
const LeadsSummaryThisMonth = lazy(() => import('@/components/dashboard/DashboardWidgetWrappers').then(m => ({ default: m.LeadsSummaryThisMonth })));
const QuotaOverview = lazy(() => import('@/components/quota/QuotaStatusCard').then(m => ({ default: m.QuotaOverview })));

// Analytics components
const AnalyticsSessionsCard = lazy(() => import('@/components/holdco-views/analytics/AnalyticsSessionsCard').then(m => ({ default: m.AnalyticsSessionsCard })));
const AnalyticsUsersCard = lazy(() => import('@/components/holdco-views/analytics/AnalyticsUsersCard').then(m => ({ default: m.AnalyticsUsersCard })));
const AnalyticsPageviewsCard = lazy(() => import('@/components/holdco-views/analytics/AnalyticsPageviewsCard').then(m => ({ default: m.AnalyticsPageviewsCard })));
const AnalyticsBounceRateCard = lazy(() => import('@/components/holdco-views/analytics/AnalyticsBounceRateCard').then(m => ({ default: m.AnalyticsBounceRateCard })));
const AnalyticsAvgSessionDurationCard = lazy(() => import('@/components/holdco-views/analytics/AnalyticsAvgSessionDurationCard').then(m => ({ default: m.AnalyticsAvgSessionDurationCard })));
const AnalyticsTrueTrafficCard = lazy(() => import('@/components/holdco-views/analytics/AnalyticsTrueTrafficCard').then(m => ({ default: m.AnalyticsTrueTrafficCard })));
const AnalyticsTrafficTrendsChart = lazy(() => import('@/components/holdco-views/analytics/AnalyticsTrafficTrendsChart').then(m => ({ default: m.AnalyticsTrafficTrendsChart })));
const AnalyticsDeviceSplitChart = lazy(() => import('@/components/holdco-views/analytics/AnalyticsDeviceSplitChart').then(m => ({ default: m.AnalyticsDeviceSplitChart })));
const AnalyticsTrafficDriversChart = lazy(() => import('@/components/holdco-views/analytics/AnalyticsTrafficDriversChart').then(m => ({ default: m.AnalyticsTrafficDriversChart })));
const AnalyticsTopPagesTable = lazy(() => import('@/components/holdco-views/analytics/AnalyticsTopPagesTable').then(m => ({ default: m.AnalyticsTopPagesTable })));
const AnalyticsTrafficSourcesTable = lazy(() => import('@/components/holdco-views/analytics/AnalyticsTrafficSourcesTable').then(m => ({ default: m.AnalyticsTrafficSourcesTable })));
const AnalyticsTopReferrersTable = lazy(() => import('@/components/holdco-views/analytics/AnalyticsTopReferrersTable').then(m => ({ default: m.AnalyticsTopReferrersTable })));
const AnalyticsHostnameTable = lazy(() => import('@/components/holdco-views/analytics/AnalyticsHostnameTable').then(m => ({ default: m.AnalyticsHostnameTable })));
const AnalyticsSessionSourcesTable = lazy(() => import('@/components/holdco-views/analytics/AnalyticsSessionSourcesTable').then(m => ({ default: m.AnalyticsSessionSourcesTable })));
const AnalyticsSessionMediumTable = lazy(() => import('@/components/holdco-views/analytics/AnalyticsSessionMediumTable').then(m => ({ default: m.AnalyticsSessionMediumTable })));
const AnalyticsTrafficCountriesGrid = lazy(() => import('@/components/holdco-views/analytics/AnalyticsTrafficCountriesGrid').then(m => ({ default: m.AnalyticsTrafficCountriesGrid })));

// Route-specific: Scale 2Y3X, SEO Dashboard, SEO Report
const Scale2Y3XFlaggedIdeas = lazy(() => import('@/components/holdco-views/Scale2Y3XFlaggedIdeas').then(m => ({ default: m.Scale2Y3XFlaggedIdeas })));
const TeamAvatars2Y3X = lazy(() => import('@/components/dashboard/TeamAvatars2Y3X').then(m => ({ default: m.default })));
const Scale2Y3XGoalsCard = lazy(() => import('@/components/scale-2y3x/dashboard/GoalsCard').then(m => ({ default: m.default })));
const Scale2Y3XGrowthLabTeamCard = lazy(() => import('@/components/scale-2y3x/dashboard/GrowthLabTeamCard').then(m => ({ default: m.default })));
const Scale2Y3XActiveSprintsCard = lazy(() => import('@/components/scale-2y3x/dashboard/ActiveSprintsCard').then(m => ({ default: m.default })));
const Scale2Y3XIdeasCard = lazy(() => import('@/components/scale-2y3x/dashboard/IdeasCard').then(m => ({ default: m.default })));
const Scale2Y3XTimeProgress = lazy(() => import('@/components/scale-2y3x/dashboard/TimeProgress').then(m => ({ default: m.default })));
const Scale2Y3XQualitativeTaskProgress = lazy(() => import('@/components/scale-2y3x/dashboard/QualitativeTaskProgress').then(m => ({ default: m.default })));
const Scale2Y3XGoalProgress = lazy(() => import('@/components/scale-2y3x/dashboard/GoalProgress').then(m => ({ default: m.default })));
const Scale2Y3XTeamMembersList = lazy(() => import('@/components/scale-2y3x/dashboard/TeamMembersList').then(m => ({ default: m.default })));
const SeoDashboardOverviewCards = lazy(() => import('@/components/holdco-views/SeoDashboardOverviewCards').then(m => ({ default: m.SeoDashboardOverviewCards })));
const SeoReportQueryCountingCard = lazy(() => import('@/components/holdco-views/seo-report/SeoReportQueryCountingCard').then(m => ({ default: m.SeoReportQueryCountingCard })));
const SeoReportBrandedVsNonBrandedCard = lazy(() => import('@/components/holdco-views/seo-report/SeoReportBrandedVsNonBrandedCard').then(m => ({ default: m.SeoReportBrandedVsNonBrandedCard })));
const SeoReportGSCMonthlyChart = lazy(() => import('@/components/holdco-views/seo-report/SeoReportGSCMonthlyChart').then(m => ({ default: m.SeoReportGSCMonthlyChart })));

// Sales Insights components
const SalesInsightsTotalProposalsCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsTotalProposalsCard').then(m => ({ default: m.SalesInsightsTotalProposalsCard })));
const SalesInsightsDraftCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsDraftCard').then(m => ({ default: m.SalesInsightsDraftCard })));
const SalesInsightsRespondedCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsRespondedCard').then(m => ({ default: m.SalesInsightsRespondedCard })));
const SalesInsightsAwardedCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsAwardedCard').then(m => ({ default: m.SalesInsightsAwardedCard })));
const SalesInsightsTotalValueAwardedCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsTotalValueAwardedCard').then(m => ({ default: m.SalesInsightsTotalValueAwardedCard })));
const SalesInsightsTotalValueCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsTotalValueCard').then(m => ({ default: m.SalesInsightsTotalValueCard })));
const SalesInsightsAvgViewsPerProposalCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsAvgViewsPerProposalCard').then(m => ({ default: m.SalesInsightsAvgViewsPerProposalCard })));
const SalesInsightsAvgViewsPerAwardedCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsAvgViewsPerAwardedCard').then(m => ({ default: m.SalesInsightsAvgViewsPerAwardedCard })));
const SalesInsightsAwardedVsRespondedChart = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsAwardedVsRespondedChart').then(m => ({ default: m.SalesInsightsAwardedVsRespondedChart })));
const SalesInsightsRespondedVsTotalChart = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsRespondedVsTotalChart').then(m => ({ default: m.SalesInsightsRespondedVsTotalChart })));
const SalesInsightsAvgDealValueAwardedCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsAvgDealValueAwardedCard').then(m => ({ default: m.SalesInsightsAvgDealValueAwardedCard })));
const SalesInsightsAvgSalesCycleCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsAvgSalesCycleCard').then(m => ({ default: m.SalesInsightsAvgSalesCycleCard })));
const SalesInsightsWeightedAvgDealCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsWeightedAvgDealCard').then(m => ({ default: m.SalesInsightsWeightedAvgDealCard })));

// Contacts (/contacts) holdco cards
const ContactsTotalContactsCard = lazy(() => import('@/components/holdco-views/contacts/ContactsTotalContactsCard').then(m => ({ default: m.ContactsTotalContactsCard })));
const ContactsTotalOrganizationsCard = lazy(() => import('@/components/holdco-views/contacts/ContactsTotalOrganizationsCard').then(m => ({ default: m.ContactsTotalOrganizationsCard })));
const ContactsTotalModulesCard = lazy(() => import('@/components/holdco-views/contacts/ContactsTotalModulesCard').then(m => ({ default: m.ContactsTotalModulesCard })));
const ContactsTotalActivitiesCard = lazy(() => import('@/components/holdco-views/contacts/ContactsTotalActivitiesCard').then(m => ({ default: m.ContactsTotalActivitiesCard })));

// User Activity (/admin/user-activity-tracking) holdco cards
const UserActivityTotalTimeCard = lazy(() => import('@/components/holdco-views/user-activity/UserActivityTotalTimeCard').then(m => ({ default: m.UserActivityTotalTimeCard })));
const UserActivityTotalVisitsCard = lazy(() => import('@/components/holdco-views/user-activity/UserActivityTotalVisitsCard').then(m => ({ default: m.UserActivityTotalVisitsCard })));
const UserActivityUniqueRoutesCard = lazy(() => import('@/components/holdco-views/user-activity/UserActivityUniqueRoutesCard').then(m => ({ default: m.UserActivityUniqueRoutesCard })));
const UserActivityUniqueUsersCard = lazy(() => import('@/components/holdco-views/user-activity/UserActivityUniqueUsersCard').then(m => ({ default: m.UserActivityUniqueUsersCard })));
const UserActivityOverTimeChartCard = lazy(() => import('@/components/holdco-views/user-activity/UserActivityOverTimeChartCard').then(m => ({ default: m.UserActivityOverTimeChartCard })));
const UserActivityTimeByBrandChartCard = lazy(() => import('@/components/holdco-views/user-activity/UserActivityTimeByBrandChartCard').then(m => ({ default: m.UserActivityTimeByBrandChartCard })));

// User Time (/admin/user-time-tracking) holdco cards
const UserTimeTotalMinutesCard = lazy(() => import('@/components/holdco-views/user-time/UserTimeTotalMinutesCard').then(m => ({ default: m.UserTimeTotalMinutesCard })));
const UserTimeFoundationalCard = lazy(() => import('@/components/holdco-views/user-time/UserTimeFoundationalCard').then(m => ({ default: m.UserTimeFoundationalCard })));
const UserTimeGrowthCard = lazy(() => import('@/components/holdco-views/user-time/UserTimeGrowthCard').then(m => ({ default: m.UserTimeGrowthCard })));
const UserTimeRecurringCard = lazy(() => import('@/components/holdco-views/user-time/UserTimeRecurringCard').then(m => ({ default: m.UserTimeRecurringCard })));
const UserTimeLoggedOverTimeChartCard = lazy(() => import('@/components/holdco-views/user-time/UserTimeLoggedOverTimeChartCard').then(m => ({ default: m.UserTimeLoggedOverTimeChartCard })));
const UserTimeByTaskTypeChartCard = lazy(() => import('@/components/holdco-views/user-time/UserTimeByTaskTypeChartCard').then(m => ({ default: m.UserTimeByTaskTypeChartCard })));
const UserTimeBySourceChartCard = lazy(() => import('@/components/holdco-views/user-time/UserTimeBySourceChartCard').then(m => ({ default: m.UserTimeBySourceChartCard })));
const UserTimeByBrandChartCard = lazy(() => import('@/components/holdco-views/user-time/UserTimeByBrandChartCard').then(m => ({ default: m.UserTimeByBrandChartCard })));

// Growth Standup (/growth-plan) - Day Start / Day End
const GrowthStandupDayStartCard = lazy(() => import('@/components/holdco-views/growth-standup/GrowthStandupDayStartCard').then(m => ({ default: m.GrowthStandupDayStartCard })));
const GrowthStandupDayEndCard = lazy(() => import('@/components/holdco-views/growth-standup/GrowthStandupDayEndCard').then(m => ({ default: m.GrowthStandupDayEndCard })));
const GrowthStandupTodaySummaryCard = lazy(() => import('@/components/holdco-views/growth-standup/GrowthStandupTodaySummaryCard').then(m => ({ default: m.GrowthStandupTodaySummaryCard })));
const GrowthStandupDayStartOnlyCard = lazy(() => import('@/components/holdco-views/growth-standup/GrowthStandupDayStartOnlyCard').then(m => ({ default: m.GrowthStandupDayStartOnlyCard })));
const GrowthStandupDayEndOnlyCard = lazy(() => import('@/components/holdco-views/growth-standup/GrowthStandupDayEndOnlyCard').then(m => ({ default: m.GrowthStandupDayEndOnlyCard })));

// User Settings (/user-settings) holdco cards
const UserSettingsFavoritesCard = lazy(() => import('@/components/holdco-views/user-settings/UserSettingsFavoritesCard').then(m => ({ default: m.UserSettingsFavoritesCard })));
const UserSettingsLanguagesCard = lazy(() => import('@/components/holdco-views/user-settings/UserSettingsLanguagesCard').then(m => ({ default: m.UserSettingsLanguagesCard })));
const UserSettingsThemesCard = lazy(() => import('@/components/holdco-views/user-settings/UserSettingsThemesCard').then(m => ({ default: m.UserSettingsThemesCard })));

// LMS Dashboard (/lms/dashboard) holdco cards
const LmsDashboardTotalCoursesCard = lazy(() => import('@/components/holdco-views/lms-dashboard/LmsDashboardTotalCoursesCard').then(m => ({ default: m.LmsDashboardTotalCoursesCard })));
const LmsDashboardPublishedCard = lazy(() => import('@/components/holdco-views/lms-dashboard/LmsDashboardPublishedCard').then(m => ({ default: m.LmsDashboardPublishedCard })));
const LmsDashboardDraftCard = lazy(() => import('@/components/holdco-views/lms-dashboard/LmsDashboardDraftCard').then(m => ({ default: m.LmsDashboardDraftCard })));
const LmsDashboardCompaniesEnrolledCard = lazy(() => import('@/components/holdco-views/lms-dashboard/LmsDashboardCompaniesEnrolledCard').then(m => ({ default: m.LmsDashboardCompaniesEnrolledCard })));
const LmsDashboardStudentsEnrolledCard = lazy(() => import('@/components/holdco-views/lms-dashboard/LmsDashboardStudentsEnrolledCard').then(m => ({ default: m.LmsDashboardStudentsEnrolledCard })));
const LmsDashboardCurrentlyWatchingCard = lazy(() => import('@/components/holdco-views/lms-dashboard/LmsDashboardCurrentlyWatchingCard').then(m => ({ default: m.LmsDashboardCurrentlyWatchingCard })));
const LmsDashboardCourseTagsCard = lazy(() => import('@/components/holdco-views/lms-dashboard/LmsDashboardCourseTagsCard').then(m => ({ default: m.LmsDashboardCourseTagsCard })));
const LmsDashboardLessonTagsCard = lazy(() => import('@/components/holdco-views/lms-dashboard/LmsDashboardLessonTagsCard').then(m => ({ default: m.LmsDashboardLessonTagsCard })));
const LmsDashboardTopStudentCard = lazy(() => import('@/components/holdco-views/lms-dashboard/LmsDashboardTopStudentCard').then(m => ({ default: m.LmsDashboardTopStudentCard })));
const LmsDashboardTopCompanyCard = lazy(() => import('@/components/holdco-views/lms-dashboard/LmsDashboardTopCompanyCard').then(m => ({ default: m.LmsDashboardTopCompanyCard })));
const LmsDashboardTopLessonCard = lazy(() => import('@/components/holdco-views/lms-dashboard/LmsDashboardTopLessonCard').then(m => ({ default: m.LmsDashboardTopLessonCard })));
const LmsDashboardWorstPerformingLessonCard = lazy(() => import('@/components/holdco-views/lms-dashboard/LmsDashboardWorstPerformingLessonCard').then(m => ({ default: m.LmsDashboardWorstPerformingLessonCard })));
const LmsDashboardLessonMostClapsCard = lazy(() => import('@/components/holdco-views/lms-dashboard/LmsDashboardLessonMostClapsCard').then(m => ({ default: m.LmsDashboardLessonMostClapsCard })));
const LmsDashboardStudentMostClapsCard = lazy(() => import('@/components/holdco-views/lms-dashboard/LmsDashboardStudentMostClapsCard').then(m => ({ default: m.LmsDashboardStudentMostClapsCard })));
const LmsDashboardLessonLeastClapsCard = lazy(() => import('@/components/holdco-views/lms-dashboard/LmsDashboardLessonLeastClapsCard').then(m => ({ default: m.LmsDashboardLessonLeastClapsCard })));
const LmsDashboardStudentLeastClapsCard = lazy(() => import('@/components/holdco-views/lms-dashboard/LmsDashboardStudentLeastClapsCard').then(m => ({ default: m.LmsDashboardStudentLeastClapsCard })));

// LMS Ask For More (/lms/ask-for-more) holdco cards
const LmsAskForMoreAllCard = lazy(() => import('@/components/holdco-views/lms-ask-for-more/LmsAskForMoreAllCard').then(m => ({ default: m.LmsAskForMoreAllCard })));
const LmsAskForMoreAskedCard = lazy(() => import('@/components/holdco-views/lms-ask-for-more/LmsAskForMoreAskedCard').then(m => ({ default: m.LmsAskForMoreAskedCard })));
const LmsAskForMoreConfirmedCard = lazy(() => import('@/components/holdco-views/lms-ask-for-more/LmsAskForMoreConfirmedCard').then(m => ({ default: m.LmsAskForMoreConfirmedCard })));
const LmsAskForMorePlannedCard = lazy(() => import('@/components/holdco-views/lms-ask-for-more/LmsAskForMorePlannedCard').then(m => ({ default: m.LmsAskForMorePlannedCard })));
const LmsAskForMoreRejectedCard = lazy(() => import('@/components/holdco-views/lms-ask-for-more/LmsAskForMoreRejectedCard').then(m => ({ default: m.LmsAskForMoreRejectedCard })));
const LmsAskForMoreSearchCard = lazy(() => import('@/components/holdco-views/lms-ask-for-more/LmsAskForMoreSearchCard').then(m => ({ default: m.LmsAskForMoreSearchCard })));
const LmsAskForMoreRequestsTableCard = lazy(() => import('@/components/holdco-views/lms-ask-for-more/LmsAskForMoreRequestsTableCard').then(m => ({ default: m.LmsAskForMoreRequestsTableCard })));

// Route widgets: newsfeed/leads, podcasthost, personas, fg-dashboard, events
const NewsfeedLeadsTotalSubscribersCard = lazy(() => import('@/components/holdco-views/route-widgets/newsfeed-leads/NewsfeedLeadsTotalSubscribersCard').then(m => ({ default: m.NewsfeedLeadsTotalSubscribersCard })));
const NewsfeedLeadsActiveCard = lazy(() => import('@/components/holdco-views/route-widgets/newsfeed-leads/NewsfeedLeadsActiveCard').then(m => ({ default: m.NewsfeedLeadsActiveCard })));
const NewsfeedLeadsUnsubscribedCard = lazy(() => import('@/components/holdco-views/route-widgets/newsfeed-leads/NewsfeedLeadsUnsubscribedCard').then(m => ({ default: m.NewsfeedLeadsUnsubscribedCard })));
const NewsfeedLeadsFiltersCard = lazy(() => import('@/components/holdco-views/route-widgets/newsfeed-leads/NewsfeedLeadsFiltersCard').then(m => ({ default: m.NewsfeedLeadsFiltersCard })));
const NewsfeedLeadsTableCard = lazy(() => import('@/components/holdco-views/route-widgets/newsfeed-leads/NewsfeedLeadsTableCard').then(m => ({ default: m.NewsfeedLeadsTableCard })));
const PodcasthostActiveShowsCard = lazy(() => import('@/components/holdco-views/route-widgets/podcasthost/PodcasthostActiveShowsCard').then(m => ({ default: m.PodcasthostActiveShowsCard })));
const PodcasthostUpcomingEpisodesCard = lazy(() => import('@/components/holdco-views/route-widgets/podcasthost/PodcasthostUpcomingEpisodesCard').then(m => ({ default: m.PodcasthostUpcomingEpisodesCard })));
const PodcasthostPendingConfirmationsCard = lazy(() => import('@/components/holdco-views/route-widgets/podcasthost/PodcasthostPendingConfirmationsCard').then(m => ({ default: m.PodcasthostPendingConfirmationsCard })));
const PodcasthostActiveCampaignsCard = lazy(() => import('@/components/holdco-views/route-widgets/podcasthost/PodcasthostActiveCampaignsCard').then(m => ({ default: m.PodcasthostActiveCampaignsCard })));
const PodcasthostUpcomingRecordingsCard = lazy(() => import('@/components/holdco-views/route-widgets/podcasthost/PodcasthostUpcomingRecordingsCard').then(m => ({ default: m.PodcasthostUpcomingRecordingsCard })));
const PodcasthostNeedsAttentionCard = lazy(() => import('@/components/holdco-views/route-widgets/podcasthost/PodcasthostNeedsAttentionCard').then(m => ({ default: m.PodcasthostNeedsAttentionCard })));
const PodcasthostQuickActionsCard = lazy(() => import('@/components/holdco-views/route-widgets/podcasthost/PodcasthostQuickActionsCard').then(m => ({ default: m.PodcasthostQuickActionsCard })));
const PersonasTotalPersonasCard = lazy(() => import('@/components/holdco-views/route-widgets/personas/PersonasTotalPersonasCard').then(m => ({ default: m.PersonasTotalPersonasCard })));
const PersonasBriefsCard = lazy(() => import('@/components/holdco-views/route-widgets/personas/PersonasBriefsCard').then(m => ({ default: m.PersonasBriefsCard })));
const PersonasJobRolesCard = lazy(() => import('@/components/holdco-views/route-widgets/personas/PersonasJobRolesCard').then(m => ({ default: m.PersonasJobRolesCard })));
const PersonasAverageAgeCard = lazy(() => import('@/components/holdco-views/route-widgets/personas/PersonasAverageAgeCard').then(m => ({ default: m.PersonasAverageAgeCard })));
const PersonasFiltersCard = lazy(() => import('@/components/holdco-views/route-widgets/personas/PersonasFiltersCard').then(m => ({ default: m.PersonasFiltersCard })));
const PersonasListCard = lazy(() => import('@/components/holdco-views/route-widgets/personas/PersonasListCard').then(m => ({ default: m.PersonasListCard })));
const FgTotalProjectsCard = lazy(() => import('@/components/holdco-views/route-widgets/fg-dashboard/FgTotalProjectsCard').then(m => ({ default: m.FgTotalProjectsCard })));
const FgFocusGroups30dCard = lazy(() => import('@/components/holdco-views/route-widgets/fg-dashboard/FgFocusGroups30dCard').then(m => ({ default: m.FgFocusGroups30dCard })));
const FgTotalSessionsCard = lazy(() => import('@/components/holdco-views/route-widgets/fg-dashboard/FgTotalSessionsCard').then(m => ({ default: m.FgTotalSessionsCard })));
const FgTotalResponsesCard = lazy(() => import('@/components/holdco-views/route-widgets/fg-dashboard/FgTotalResponsesCard').then(m => ({ default: m.FgTotalResponsesCard })));
const FgSessionsByCategoryCard = lazy(() => import('@/components/holdco-views/route-widgets/fg-dashboard/FgSessionsByCategoryCard').then(m => ({ default: m.FgSessionsByCategoryCard })));
const FgResonanceChartCard = lazy(() => import('@/components/holdco-views/route-widgets/fg-dashboard/FgResonanceChartCard').then(m => ({ default: m.FgResonanceChartCard })));
const FgEmotionalReactionsCard = lazy(() => import('@/components/holdco-views/route-widgets/fg-dashboard/FgEmotionalReactionsCard').then(m => ({ default: m.FgEmotionalReactionsCard })));
const FgAuthenticityChartCard = lazy(() => import('@/components/holdco-views/route-widgets/fg-dashboard/FgAuthenticityChartCard').then(m => ({ default: m.FgAuthenticityChartCard })));
const EventsTotalEventsCard = lazy(() => import('@/components/holdco-views/route-widgets/events/EventsTotalEventsCard').then(m => ({ default: m.EventsTotalEventsCard })));
const EventsUpcomingCard = lazy(() => import('@/components/holdco-views/route-widgets/events/EventsUpcomingCard').then(m => ({ default: m.EventsUpcomingCard })));
const EventsPendingLeadsCard = lazy(() => import('@/components/holdco-views/route-widgets/events/EventsPendingLeadsCard').then(m => ({ default: m.EventsPendingLeadsCard })));
const EventsActiveCampaignsCard = lazy(() => import('@/components/holdco-views/route-widgets/events/EventsActiveCampaignsCard').then(m => ({ default: m.EventsActiveCampaignsCard })));
const EventsUpcomingListCard = lazy(() => import('@/components/holdco-views/route-widgets/events/EventsUpcomingListCard').then(m => ({ default: m.EventsUpcomingListCard })));
const EventsQuickActionsCard = lazy(() => import('@/components/holdco-views/route-widgets/events/EventsQuickActionsCard').then(m => ({ default: m.EventsQuickActionsCard })));

export interface DashboardComponentConfig {
  key: string;
  label: string;
  component: ComponentType<any>;
  defaultVisible: boolean;
  defaultOrder: number;
  category: 'header' | 'metrics' | 'insights' | 'activity' | 'calendar' | 'quota';
  gridConfig?: {
    colSpan?: number;
    lgColSpan?: number;
  };
  requiresBrandId?: boolean;
  requiresQuotaStatus?: boolean;
  /** If false, component is hidden from holdco view Configure Dashboard and defaults. Omit or true = available. */
  availableInHoldco?: boolean;
}

/**
 * Components that can be shown in holdco view dashboard.
 * Only includes DASHBOARD_COMPONENTS (which have React components).
 * Route-specific components are handled separately in the config dialog but not included here
 * since they don't have React components yet.
 * @param dbSettings - Optional map from DB (component_key -> available_in_holdco). 
 *   If a component is explicitly set to false in dbSettings, it's excluded.
 *   Otherwise, dashboard components are included by default.
 */
export const getHoldcoAvailableComponents = (
  dbSettings?: Record<string, boolean>
): DashboardComponentConfig[] => {
  // Only return dashboard components (which have React components)
  // Route-specific components will show in the config dialog but won't be included here
  // until we add React components for them
  return DASHBOARD_COMPONENTS.filter((c) => {
    // If component is explicitly disabled in dbSettings, exclude it
    if (dbSettings && c.key in dbSettings && dbSettings[c.key] === false) {
      return false;
    }
    // Otherwise include if availableInHoldco is not explicitly false
    return c.availableInHoldco !== false;
  });
};

export const DASHBOARD_COMPONENTS: DashboardComponentConfig[] = [
  {
    key: 'team_avatars',
    label: 'Team Avatars',
    component: TeamAvatars,
    defaultVisible: true,
    defaultOrder: 0,
    category: 'header',
  },
  {
    key: 'team_on_vacation',
    label: 'Team on Vacation',
    component: TeamOnVacation,
    defaultVisible: true,
    defaultOrder: 0.5,
    category: 'header',
  },
  {
    key: 'fte_tasks_list',
    label: 'FTE Tasks List',
    component: FteTasksList,
    defaultVisible: true,
    defaultOrder: 1,
    category: 'activity',
  },
  {
    key: 'upcoming_appointments',
    label: 'Upcoming Appointments',
    component: UpcomingAppointments,
    defaultVisible: true,
    defaultOrder: 2,
    category: 'calendar',
    requiresBrandId: true,
  },
  {
    key: 'active_brands',
    label: 'Active Brands',
    component: ActiveBrands,
    defaultVisible: true,
    defaultOrder: 3,
    category: 'metrics',
    gridConfig: { colSpan: 1 },
  },
  {
    key: 'tasks_due_today',
    label: 'Tasks Due Today',
    component: TasksDueToday,
    defaultVisible: true,
    defaultOrder: 4,
    category: 'metrics',
    gridConfig: { colSpan: 1 },
  },
  {
    key: 'tasks_completed_today',
    label: 'Tasks Completed Today',
    component: TasksCompletedToday,
    defaultVisible: true,
    defaultOrder: 5,
    category: 'metrics',
    gridConfig: { colSpan: 1 },
  },
  {
    key: 'leads_summary',
    label: 'Leads Summary (All)',
    component: LeadsSummary,
    defaultVisible: false,
    defaultOrder: 5.75,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'leads_summary_today',
    label: 'Leads (Today)',
    component: LeadsSummaryToday,
    defaultVisible: false,
    defaultOrder: 5.8,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'leads_summary_last_7_days',
    label: 'Leads (Last 7 days)',
    component: LeadsSummaryLast7Days,
    defaultVisible: false,
    defaultOrder: 5.85,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'leads_summary_last_14_days',
    label: 'Leads (Last 14 days)',
    component: LeadsSummaryLast14Days,
    defaultVisible: false,
    defaultOrder: 5.9,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'leads_summary_last_month',
    label: 'Leads (Last month)',
    component: LeadsSummaryLastMonth,
    defaultVisible: false,
    defaultOrder: 5.95,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'leads_summary_this_month',
    label: 'Leads (This month)',
    component: LeadsSummaryThisMonth,
    defaultVisible: true,
    defaultOrder: 6,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'leads_daily_trends',
    label: 'Leads Daily Trends',
    component: LeadsDailyTrends,
    defaultVisible: false,
    defaultOrder: 6.1,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'leads_channels',
    label: 'Leads Channels',
    component: LeadsChannelsChart,
    defaultVisible: false,
    defaultOrder: 6.2,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'leads_medium',
    label: 'Leads Medium',
    component: LeadsMediumChart,
    defaultVisible: false,
    defaultOrder: 6.3,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'leads_sources',
    label: 'Leads Sources',
    component: LeadsSourcesChart,
    defaultVisible: false,
    defaultOrder: 6.4,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'leads_channels_by_product',
    label: 'Leads Channels by Product',
    component: LeadsChannelsByProductChart,
    defaultVisible: false,
    defaultOrder: 6.45,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'leads_medium_by_product',
    label: 'Leads by Product (Donut)',
    component: LeadsMediumByProductChart,
    defaultVisible: false,
    defaultOrder: 6.5,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'conversion_funnel',
    label: 'Conversion Funnel (All)',
    component: ConversionFunnel,
    defaultVisible: false,
    defaultOrder: 6.75,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'conversion_funnel_today',
    label: 'Conversion Funnel (Today)',
    component: ConversionFunnelToday,
    defaultVisible: false,
    defaultOrder: 6.8,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'conversion_funnel_last_7_days',
    label: 'Conversion Funnel (Last 7 days)',
    component: ConversionFunnelLast7Days,
    defaultVisible: false,
    defaultOrder: 6.85,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'conversion_funnel_last_14_days',
    label: 'Conversion Funnel (Last 14 days)',
    component: ConversionFunnelLast14Days,
    defaultVisible: false,
    defaultOrder: 6.9,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'conversion_funnel_last_month',
    label: 'Conversion Funnel (Last month)',
    component: ConversionFunnelLastMonth,
    defaultVisible: false,
    defaultOrder: 6.95,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'conversion_funnel_this_month',
    label: 'Conversion Funnel (This month)',
    component: ConversionFunnelThisMonth,
    defaultVisible: true,
    defaultOrder: 7,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'project_progress',
    label: 'Growth Playbook Progress',
    component: ProjectProgress,
    defaultVisible: true,
    defaultOrder: 8,
    category: 'insights',
    gridConfig: { lgColSpan: 2 },
  },
  {
    key: 'project_progress_score',
    label: 'Progress Score',
    component: ProjectProgressScore,
    defaultVisible: true,
    defaultOrder: 9,
    category: 'insights',
    gridConfig: { lgColSpan: 1 },
  },
  {
    key: 'team_activity_feed',
    label: 'Team Activity Feed',
    component: TeamActivityFeed,
    defaultVisible: true,
    defaultOrder: 10,
    category: 'activity',
    gridConfig: { lgColSpan: 1 },
  },
  {
    key: 'tasks_assigned_grid',
    label: 'Tasks Assigned Grid',
    component: TasksAssignedGrid,
    defaultVisible: true,
    defaultOrder: 11,
    category: 'activity',
    gridConfig: { lgColSpan: 1 },
  },
  {
    key: 'visuals_to_be_approved',
    label: 'Visuals to be Approved',
    component: VisualsToBeApproved,
    defaultVisible: true,
    defaultOrder: 12,
    category: 'activity',
  },
  {
    key: 'weekly_calendar_view',
    label: 'Weekly Calendar View',
    component: WeeklyCalendarView,
    defaultVisible: true,
    defaultOrder: 13,
    category: 'calendar',
  },
  {
    key: 'quota_overview',
    label: 'Monthly Quotas',
    component: QuotaOverview,
    defaultVisible: true,
    defaultOrder: 14,
    category: 'quota',
    requiresQuotaStatus: true,
  },
  // Analytics metric cards
  {
    key: 'analytics_sessions',
    label: 'Sessions',
    component: AnalyticsSessionsCard,
    defaultVisible: false,
    defaultOrder: 100,
    category: 'metrics',
    gridConfig: { colSpan: 1 },
    requiresBrandId: true,
  },
  {
    key: 'analytics_users',
    label: 'Users',
    component: AnalyticsUsersCard,
    defaultVisible: false,
    defaultOrder: 101,
    category: 'metrics',
    gridConfig: { colSpan: 1 },
    requiresBrandId: true,
  },
  {
    key: 'analytics_pageviews',
    label: 'Page Views',
    component: AnalyticsPageviewsCard,
    defaultVisible: false,
    defaultOrder: 102,
    category: 'metrics',
    gridConfig: { colSpan: 1 },
    requiresBrandId: true,
  },
  {
    key: 'analytics_bounce_rate',
    label: 'Bounce Rate',
    component: AnalyticsBounceRateCard,
    defaultVisible: false,
    defaultOrder: 103,
    category: 'metrics',
    gridConfig: { colSpan: 1 },
    requiresBrandId: true,
  },
  {
    key: 'analytics_avg_session_duration',
    label: 'Avg Session Duration',
    component: AnalyticsAvgSessionDurationCard,
    defaultVisible: false,
    defaultOrder: 104,
    category: 'metrics',
    gridConfig: { colSpan: 1 },
    requiresBrandId: true,
  },
  {
    key: 'analytics_true_traffic',
    label: 'True Traffic (Users)',
    component: AnalyticsTrueTrafficCard,
    defaultVisible: false,
    defaultOrder: 105,
    category: 'metrics',
    gridConfig: { colSpan: 1 },
    requiresBrandId: true,
  },
  // Analytics charts
  {
    key: 'analytics_traffic_trends_chart',
    label: 'Traffic Trends Chart',
    component: AnalyticsTrafficTrendsChart,
    defaultVisible: false,
    defaultOrder: 200,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'analytics_device_split_chart',
    label: 'Device Split Chart',
    component: AnalyticsDeviceSplitChart,
    defaultVisible: false,
    defaultOrder: 201,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'analytics_traffic_drivers_chart',
    label: 'Traffic Drivers Chart',
    component: AnalyticsTrafficDriversChart,
    defaultVisible: false,
    defaultOrder: 202,
    category: 'insights',
    requiresBrandId: true,
  },
  // Analytics tables
  {
    key: 'analytics_top_pages_table',
    label: 'Top-20 Pages Table',
    component: AnalyticsTopPagesTable,
    defaultVisible: false,
    defaultOrder: 300,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'analytics_traffic_sources_table',
    label: 'Traffic Sources Table',
    component: AnalyticsTrafficSourcesTable,
    defaultVisible: false,
    defaultOrder: 301,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'analytics_top_referrers_table',
    label: 'Top-20 Referrers Table',
    component: AnalyticsTopReferrersTable,
    defaultVisible: false,
    defaultOrder: 302,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'analytics_hostname_table',
    label: 'Traffic by Hostname Table',
    component: AnalyticsHostnameTable,
    defaultVisible: false,
    defaultOrder: 303,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'analytics_session_sources_table',
    label: 'Top-20 Session Sources Table',
    component: AnalyticsSessionSourcesTable,
    defaultVisible: false,
    defaultOrder: 304,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'analytics_session_medium_table',
    label: 'Top-20 Session Medium Table',
    component: AnalyticsSessionMediumTable,
    defaultVisible: false,
    defaultOrder: 305,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'analytics_traffic_countries_grid',
    label: 'Traffic Countries Grid',
    component: AnalyticsTrafficCountriesGrid,
    defaultVisible: false,
    defaultOrder: 306,
    category: 'insights',
    requiresBrandId: true,
  },
  // Scale 2Y3X Dashboard
  {
    key: 'scale_2y3x_team_avatars',
    label: 'Team Avatars',
    component: TeamAvatars2Y3X,
    defaultVisible: false,
    defaultOrder: 398,
    category: 'header',
    requiresBrandId: true,
  },
  {
    key: 'scale_2y3x_goals_card',
    label: 'Goals Card',
    component: Scale2Y3XGoalsCard,
    defaultVisible: false,
    defaultOrder: 399,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'scale_2y3x_growth_lab_team_card',
    label: 'Growth Lab Team Card',
    component: Scale2Y3XGrowthLabTeamCard,
    defaultVisible: false,
    defaultOrder: 400,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'scale_2y3x_active_sprints_card',
    label: 'Active Sprints Card',
    component: Scale2Y3XActiveSprintsCard,
    defaultVisible: false,
    defaultOrder: 401,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'scale_2y3x_ideas_card',
    label: 'Ideas Card',
    component: Scale2Y3XIdeasCard,
    defaultVisible: false,
    defaultOrder: 402,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'scale_2y3x_flagged_ideas',
    label: 'Flagged Ideas to Revisit',
    component: Scale2Y3XFlaggedIdeas,
    defaultVisible: false,
    defaultOrder: 403,
    category: 'activity',
    requiresBrandId: true,
  },
  {
    key: 'scale_2y3x_time_progress',
    label: 'Time Progress',
    component: Scale2Y3XTimeProgress,
    defaultVisible: false,
    defaultOrder: 404,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'scale_2y3x_qualitative_task_progress',
    label: 'Qualitative Task Progress',
    component: Scale2Y3XQualitativeTaskProgress,
    defaultVisible: false,
    defaultOrder: 405,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'scale_2y3x_goal_progress',
    label: 'Goal Progress',
    component: Scale2Y3XGoalProgress,
    defaultVisible: false,
    defaultOrder: 406,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'scale_2y3x_team_members_list',
    label: 'Team Members List',
    component: Scale2Y3XTeamMembersList,
    defaultVisible: false,
    defaultOrder: 407,
    category: 'activity',
    requiresBrandId: true,
  },
  // SEO Dashboard
  {
    key: 'seo_dashboard_overview_cards',
    label: 'SEO Overview Cards',
    component: SeoDashboardOverviewCards,
    defaultVisible: false,
    defaultOrder: 410,
    category: 'metrics',
    requiresBrandId: true,
  },
  // SEO Report
  {
    key: 'seo_report_query_counting',
    label: 'Query Counting Card',
    component: SeoReportQueryCountingCard,
    defaultVisible: false,
    defaultOrder: 411,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'seo_report_branded_vs_nonbranded',
    label: 'Branded vs Non-Branded',
    component: SeoReportBrandedVsNonBrandedCard,
    defaultVisible: false,
    defaultOrder: 412,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'seo_report_gsc_monthly_chart',
    label: 'GSC Monthly Chart',
    component: SeoReportGSCMonthlyChart,
    defaultVisible: false,
    defaultOrder: 413,
    category: 'insights',
    requiresBrandId: true,
  },
  // Sales Insights
  {
    key: 'sales_insights_total_proposals',
    label: 'Total Proposals',
    component: SalesInsightsTotalProposalsCard,
    defaultVisible: false,
    defaultOrder: 500,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_draft',
    label: 'Draft Proposals',
    component: SalesInsightsDraftCard,
    defaultVisible: false,
    defaultOrder: 501,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_responded',
    label: 'Responded Proposals',
    component: SalesInsightsRespondedCard,
    defaultVisible: false,
    defaultOrder: 502,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_awarded',
    label: 'Awarded Proposals',
    component: SalesInsightsAwardedCard,
    defaultVisible: false,
    defaultOrder: 503,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_total_value_awarded',
    label: 'Total Value Awarded',
    component: SalesInsightsTotalValueAwardedCard,
    defaultVisible: false,
    defaultOrder: 504,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_total_value',
    label: 'Total Value',
    component: SalesInsightsTotalValueCard,
    defaultVisible: false,
    defaultOrder: 505,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_avg_views_per_proposal',
    label: 'Avg Views per Proposal',
    component: SalesInsightsAvgViewsPerProposalCard,
    defaultVisible: false,
    defaultOrder: 506,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_avg_views_per_awarded',
    label: 'Avg Views per Awarded',
    component: SalesInsightsAvgViewsPerAwardedCard,
    defaultVisible: false,
    defaultOrder: 507,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_awarded_vs_responded_chart',
    label: 'Awarded vs Responded Chart',
    component: SalesInsightsAwardedVsRespondedChart,
    defaultVisible: false,
    defaultOrder: 508,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_responded_vs_total_chart',
    label: 'Responded vs Total Chart',
    component: SalesInsightsRespondedVsTotalChart,
    defaultVisible: false,
    defaultOrder: 509,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_avg_deal_value_awarded',
    label: 'Avg $ Deal Awarded',
    component: SalesInsightsAvgDealValueAwardedCard,
    defaultVisible: false,
    defaultOrder: 510,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_avg_sales_cycle',
    label: 'Avg Sales Cycle',
    component: SalesInsightsAvgSalesCycleCard,
    defaultVisible: false,
    defaultOrder: 511,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_weighted_avg_deal',
    label: 'Weighted Avg $ Deal',
    component: SalesInsightsWeightedAvgDealCard,
    defaultVisible: false,
    defaultOrder: 512,
    category: 'metrics',
    requiresBrandId: true,
  },
  // Contacts (/contacts)
  {
    key: 'contacts_total_contacts',
    label: 'Total Contacts',
    component: ContactsTotalContactsCard,
    defaultVisible: false,
    defaultOrder: 600,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'contacts_total_organizations',
    label: 'Total Organizations',
    component: ContactsTotalOrganizationsCard,
    defaultVisible: false,
    defaultOrder: 601,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'contacts_total_modules',
    label: 'Total Modules',
    component: ContactsTotalModulesCard,
    defaultVisible: false,
    defaultOrder: 602,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'contacts_total_activities',
    label: 'Total Activities',
    component: ContactsTotalActivitiesCard,
    defaultVisible: false,
    defaultOrder: 603,
    category: 'metrics',
    requiresBrandId: true,
  },
  // User Activity (/admin/user-activity-tracking)
  {
    key: 'user_activity_total_time',
    label: 'Total Time',
    component: UserActivityTotalTimeCard,
    defaultVisible: false,
    defaultOrder: 700,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'user_activity_total_visits',
    label: 'Total Visits',
    component: UserActivityTotalVisitsCard,
    defaultVisible: false,
    defaultOrder: 701,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'user_activity_unique_routes',
    label: 'Unique Routes',
    component: UserActivityUniqueRoutesCard,
    defaultVisible: false,
    defaultOrder: 702,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'user_activity_unique_users',
    label: 'Unique Users',
    component: UserActivityUniqueUsersCard,
    defaultVisible: false,
    defaultOrder: 703,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'user_activity_over_time_chart',
    label: 'Activity Over Time',
    component: UserActivityOverTimeChartCard,
    defaultVisible: false,
    defaultOrder: 704,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'user_activity_time_by_brand_chart',
    label: 'Time by Brand',
    component: UserActivityTimeByBrandChartCard,
    defaultVisible: false,
    defaultOrder: 705,
    category: 'insights',
    requiresBrandId: true,
  },
  // User Time (/admin/user-time-tracking)
  {
    key: 'user_time_total_minutes',
    label: 'Total Time',
    component: UserTimeTotalMinutesCard,
    defaultVisible: false,
    defaultOrder: 710,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'user_time_foundational',
    label: 'Foundational',
    component: UserTimeFoundationalCard,
    defaultVisible: false,
    defaultOrder: 711,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'user_time_growth',
    label: 'Growth',
    component: UserTimeGrowthCard,
    defaultVisible: false,
    defaultOrder: 712,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'user_time_recurring',
    label: 'Recurring',
    component: UserTimeRecurringCard,
    defaultVisible: false,
    defaultOrder: 713,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'user_time_logged_over_time_chart',
    label: 'Time Logged Over Time',
    component: UserTimeLoggedOverTimeChartCard,
    defaultVisible: false,
    defaultOrder: 714,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'user_time_by_task_type_chart',
    label: 'By Task Type',
    component: UserTimeByTaskTypeChartCard,
    defaultVisible: false,
    defaultOrder: 715,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'user_time_by_source_chart',
    label: 'By Source',
    component: UserTimeBySourceChartCard,
    defaultVisible: false,
    defaultOrder: 716,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'user_time_by_brand_chart',
    label: 'By Brand',
    component: UserTimeByBrandChartCard,
    defaultVisible: false,
    defaultOrder: 717,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'growth_standup_day_start',
    label: 'Day Start',
    component: GrowthStandupDayStartCard,
    defaultVisible: false,
    defaultOrder: 720,
    category: 'activity',
  },
  {
    key: 'growth_standup_day_end',
    label: 'Day End',
    component: GrowthStandupDayEndCard,
    defaultVisible: false,
    defaultOrder: 721,
    category: 'activity',
  },
  {
    key: 'growth_standup_today_summary',
    label: "Today's Standup Summary",
    component: GrowthStandupTodaySummaryCard,
    defaultVisible: false,
    defaultOrder: 722,
    category: 'metrics',
  },
  {
    key: 'growth_standup_day_start_only',
    label: 'Day Start (separate)',
    component: GrowthStandupDayStartOnlyCard,
    defaultVisible: false,
    defaultOrder: 723,
    category: 'activity',
  },
  {
    key: 'growth_standup_day_end_only',
    label: 'Day End (separate)',
    component: GrowthStandupDayEndOnlyCard,
    defaultVisible: false,
    defaultOrder: 724,
    category: 'activity',
  },
  // User Settings
  {
    key: 'user_settings_favorites',
    label: 'User Settings Favorites',
    component: UserSettingsFavoritesCard,
    defaultVisible: false,
    defaultOrder: 730,
    category: 'activity',
  },
  {
    key: 'user_settings_languages',
    label: 'User Settings Languages',
    component: UserSettingsLanguagesCard,
    defaultVisible: false,
    defaultOrder: 731,
    category: 'activity',
  },
  {
    key: 'user_settings_themes',
    label: 'User Settings Themes',
    component: UserSettingsThemesCard,
    defaultVisible: false,
    defaultOrder: 732,
    category: 'activity',
  },
  // LMS Dashboard (/lms/dashboard)
  { key: 'lms_dashboard_total_courses', label: 'Total Courses', component: LmsDashboardTotalCoursesCard, defaultVisible: false, defaultOrder: 740, category: 'metrics', requiresBrandId: true },
  { key: 'lms_dashboard_published', label: 'Published', component: LmsDashboardPublishedCard, defaultVisible: false, defaultOrder: 741, category: 'metrics', requiresBrandId: true },
  { key: 'lms_dashboard_draft', label: 'Draft', component: LmsDashboardDraftCard, defaultVisible: false, defaultOrder: 742, category: 'metrics', requiresBrandId: true },
  { key: 'lms_dashboard_companies_enrolled', label: 'Companies Enrolled', component: LmsDashboardCompaniesEnrolledCard, defaultVisible: false, defaultOrder: 743, category: 'metrics', requiresBrandId: true },
  { key: 'lms_dashboard_students_enrolled', label: 'Students Enrolled', component: LmsDashboardStudentsEnrolledCard, defaultVisible: false, defaultOrder: 744, category: 'metrics', requiresBrandId: true },
  { key: 'lms_dashboard_currently_watching', label: 'Currently Watching', component: LmsDashboardCurrentlyWatchingCard, defaultVisible: false, defaultOrder: 745, category: 'activity', requiresBrandId: true },
  { key: 'lms_dashboard_course_tags', label: 'Course Tags', component: LmsDashboardCourseTagsCard, defaultVisible: false, defaultOrder: 746, category: 'insights', requiresBrandId: true },
  { key: 'lms_dashboard_lesson_tags', label: 'Lesson Tags', component: LmsDashboardLessonTagsCard, defaultVisible: false, defaultOrder: 747, category: 'insights', requiresBrandId: true },
  { key: 'lms_dashboard_top_student', label: 'Top Student', component: LmsDashboardTopStudentCard, defaultVisible: false, defaultOrder: 748, category: 'insights', requiresBrandId: true },
  { key: 'lms_dashboard_top_company', label: 'Top Company', component: LmsDashboardTopCompanyCard, defaultVisible: false, defaultOrder: 749, category: 'insights', requiresBrandId: true },
  { key: 'lms_dashboard_top_lesson', label: 'Top Lesson', component: LmsDashboardTopLessonCard, defaultVisible: false, defaultOrder: 750, category: 'insights', requiresBrandId: true },
  { key: 'lms_dashboard_worst_performing_lesson', label: 'Worst Performing Lesson', component: LmsDashboardWorstPerformingLessonCard, defaultVisible: false, defaultOrder: 751, category: 'insights', requiresBrandId: true },
  { key: 'lms_dashboard_lesson_most_claps', label: 'Lesson Most Claps', component: LmsDashboardLessonMostClapsCard, defaultVisible: false, defaultOrder: 752, category: 'insights', requiresBrandId: true },
  { key: 'lms_dashboard_student_most_claps', label: 'Student Most Claps', component: LmsDashboardStudentMostClapsCard, defaultVisible: false, defaultOrder: 753, category: 'insights', requiresBrandId: true },
  { key: 'lms_dashboard_lesson_least_claps', label: 'Lesson Least Claps', component: LmsDashboardLessonLeastClapsCard, defaultVisible: false, defaultOrder: 754, category: 'insights', requiresBrandId: true },
  { key: 'lms_dashboard_student_least_claps', label: 'Student Least Claps', component: LmsDashboardStudentLeastClapsCard, defaultVisible: false, defaultOrder: 755, category: 'insights', requiresBrandId: true },

  // LMS Ask For More
  { key: 'lms_ask_for_more_all', label: 'All Requests', component: LmsAskForMoreAllCard, defaultVisible: false, defaultOrder: 756, category: 'metrics', requiresBrandId: true },
  { key: 'lms_ask_for_more_asked', label: 'Asked', component: LmsAskForMoreAskedCard, defaultVisible: false, defaultOrder: 757, category: 'metrics', requiresBrandId: true },
  { key: 'lms_ask_for_more_confirmed', label: 'Confirmed', component: LmsAskForMoreConfirmedCard, defaultVisible: false, defaultOrder: 758, category: 'metrics', requiresBrandId: true },
  { key: 'lms_ask_for_more_planned', label: 'Planned', component: LmsAskForMorePlannedCard, defaultVisible: false, defaultOrder: 759, category: 'metrics', requiresBrandId: true },
  { key: 'lms_ask_for_more_rejected', label: 'Rejected', component: LmsAskForMoreRejectedCard, defaultVisible: false, defaultOrder: 760, category: 'metrics', requiresBrandId: true },
  { key: 'lms_ask_for_more_search', label: 'Search', component: LmsAskForMoreSearchCard, defaultVisible: false, defaultOrder: 761, category: 'header', requiresBrandId: true },
  { key: 'lms_ask_for_more_requests_table', label: 'Content Requests Table', component: LmsAskForMoreRequestsTableCard, defaultVisible: false, defaultOrder: 762, category: 'activity', requiresBrandId: true },

  // Route widgets: /newsfeed/leads
  { key: 'newsfeed_leads_total_subscribers', label: 'Total Subscribers', component: NewsfeedLeadsTotalSubscribersCard, defaultVisible: false, defaultOrder: 800, category: 'metrics', requiresBrandId: true },
  { key: 'newsfeed_leads_active', label: 'Active', component: NewsfeedLeadsActiveCard, defaultVisible: false, defaultOrder: 801, category: 'metrics', requiresBrandId: true },
  { key: 'newsfeed_leads_unsubscribed', label: 'Unsubscribed', component: NewsfeedLeadsUnsubscribedCard, defaultVisible: false, defaultOrder: 802, category: 'metrics', requiresBrandId: true },
  { key: 'newsfeed_leads_filters', label: 'Search & Filters', component: NewsfeedLeadsFiltersCard, defaultVisible: false, defaultOrder: 803, category: 'header' },
  { key: 'newsfeed_leads_table', label: 'Leads Table', component: NewsfeedLeadsTableCard, defaultVisible: false, defaultOrder: 804, category: 'activity' },

  // Route widgets: /podcasthost
  { key: 'podcasthost_active_shows', label: 'Active Shows', component: PodcasthostActiveShowsCard, defaultVisible: false, defaultOrder: 810, category: 'metrics', requiresBrandId: true },
  { key: 'podcasthost_upcoming_episodes', label: 'Upcoming Episodes', component: PodcasthostUpcomingEpisodesCard, defaultVisible: false, defaultOrder: 811, category: 'metrics', requiresBrandId: true },
  { key: 'podcasthost_pending_confirmations', label: 'Pending Confirmations', component: PodcasthostPendingConfirmationsCard, defaultVisible: false, defaultOrder: 812, category: 'metrics', requiresBrandId: true },
  { key: 'podcasthost_active_campaigns', label: 'Active Campaigns', component: PodcasthostActiveCampaignsCard, defaultVisible: false, defaultOrder: 813, category: 'metrics', requiresBrandId: true },
  { key: 'podcasthost_upcoming_recordings', label: 'Upcoming Recordings', component: PodcasthostUpcomingRecordingsCard, defaultVisible: false, defaultOrder: 814, category: 'insights', requiresBrandId: true },
  { key: 'podcasthost_needs_attention', label: 'Needs Attention', component: PodcasthostNeedsAttentionCard, defaultVisible: false, defaultOrder: 815, category: 'activity', requiresBrandId: true },
  { key: 'podcasthost_quick_actions', label: 'Quick Actions', component: PodcasthostQuickActionsCard, defaultVisible: false, defaultOrder: 816, category: 'activity' },

  // Route widgets: /personas-dashboard
  { key: 'personas_total_personas', label: 'Total Personas', component: PersonasTotalPersonasCard, defaultVisible: false, defaultOrder: 820, category: 'metrics', requiresBrandId: true },
  { key: 'personas_briefs', label: 'Personas Briefs', component: PersonasBriefsCard, defaultVisible: false, defaultOrder: 821, category: 'metrics', requiresBrandId: true },
  { key: 'personas_job_roles', label: 'Job Roles', component: PersonasJobRolesCard, defaultVisible: false, defaultOrder: 822, category: 'metrics', requiresBrandId: true },
  { key: 'personas_average_age', label: 'Average Age', component: PersonasAverageAgeCard, defaultVisible: false, defaultOrder: 823, category: 'metrics', requiresBrandId: true },
  { key: 'personas_filters', label: 'Filters', component: PersonasFiltersCard, defaultVisible: false, defaultOrder: 824, category: 'header' },
  { key: 'personas_list', label: 'Personas List', component: PersonasListCard, defaultVisible: false, defaultOrder: 825, category: 'activity' },

  // Route widgets: /fg-dashboard
  { key: 'fg_total_projects', label: 'Total Projects', component: FgTotalProjectsCard, defaultVisible: false, defaultOrder: 830, category: 'metrics', requiresBrandId: true },
  { key: 'fg_focus_groups_30d', label: 'Focus Groups (30d)', component: FgFocusGroups30dCard, defaultVisible: false, defaultOrder: 831, category: 'metrics', requiresBrandId: true },
  { key: 'fg_total_sessions', label: 'Total Sessions', component: FgTotalSessionsCard, defaultVisible: false, defaultOrder: 832, category: 'metrics', requiresBrandId: true },
  { key: 'fg_total_responses', label: 'Total Responses', component: FgTotalResponsesCard, defaultVisible: false, defaultOrder: 833, category: 'metrics', requiresBrandId: true },
  { key: 'fg_sessions_by_category', label: 'Sessions by Category', component: FgSessionsByCategoryCard, defaultVisible: false, defaultOrder: 834, category: 'insights' },
  { key: 'fg_resonance_chart', label: "What Resonated vs What Didn't", component: FgResonanceChartCard, defaultVisible: false, defaultOrder: 835, category: 'insights' },
  { key: 'fg_emotional_reactions', label: 'Emotional Reactions', component: FgEmotionalReactionsCard, defaultVisible: false, defaultOrder: 836, category: 'insights' },
  { key: 'fg_authenticity_chart', label: 'Authentic vs Pushy Language', component: FgAuthenticityChartCard, defaultVisible: false, defaultOrder: 837, category: 'insights' },

  // Route widgets: /events
  { key: 'events_total_events', label: 'Total Events', component: EventsTotalEventsCard, defaultVisible: false, defaultOrder: 840, category: 'metrics', requiresBrandId: true },
  { key: 'events_upcoming', label: 'Upcoming', component: EventsUpcomingCard, defaultVisible: false, defaultOrder: 841, category: 'metrics', requiresBrandId: true },
  { key: 'events_pending_leads', label: 'Pending Leads', component: EventsPendingLeadsCard, defaultVisible: false, defaultOrder: 842, category: 'metrics', requiresBrandId: true },
  { key: 'events_active_campaigns', label: 'Active Campaigns', component: EventsActiveCampaignsCard, defaultVisible: false, defaultOrder: 843, category: 'metrics', requiresBrandId: true },
  { key: 'events_upcoming_list', label: 'Upcoming Events', component: EventsUpcomingListCard, defaultVisible: false, defaultOrder: 844, category: 'insights', requiresBrandId: true },
  { key: 'events_quick_actions', label: 'Quick Actions', component: EventsQuickActionsCard, defaultVisible: false, defaultOrder: 845, category: 'activity' },
];

export const getDefaultConfig = () =>
  DASHBOARD_COMPONENTS.map((c) => ({
    component_key: c.key,
    is_visible: c.defaultVisible,
    display_order: c.defaultOrder,
    grid_column: null as number | null,
  }));

/**
 * Custom components for routes that aren't in DASHBOARD_COMPONENTS.
 * Used for label/category lookup when rendering config dialogs.
 */
export const ROUTE_SPECIFIC_COMPONENTS: Array<{
  key: string;
  label: string;
  category: string;
}> = [
  // Analytics
  { key: 'analytics_sessions', label: 'Sessions', category: 'metrics' },
  { key: 'analytics_users', label: 'Users', category: 'metrics' },
  { key: 'analytics_pageviews', label: 'Page Views', category: 'metrics' },
  { key: 'analytics_bounce_rate', label: 'Bounce Rate', category: 'metrics' },
  { key: 'analytics_avg_session_duration', label: 'Avg Session Duration', category: 'metrics' },
  { key: 'analytics_true_traffic', label: 'True Traffic (Users)', category: 'metrics' },
  { key: 'analytics_traffic_trends_chart', label: 'Traffic Trends Chart', category: 'insights' },
  { key: 'analytics_device_split_chart', label: 'Device Split Chart', category: 'insights' },
  { key: 'analytics_traffic_drivers_chart', label: 'Traffic Drivers Chart', category: 'insights' },
  { key: 'analytics_top_pages_table', label: 'Top-20 Pages Table', category: 'insights' },
  { key: 'analytics_traffic_sources_table', label: 'Traffic Sources Table', category: 'insights' },
  { key: 'analytics_top_referrers_table', label: 'Top-20 Referrers Table', category: 'insights' },
  { key: 'analytics_hostname_table', label: 'Traffic by Hostname Table', category: 'insights' },
  { key: 'analytics_session_sources_table', label: 'Top-20 Session Sources Table', category: 'insights' },
  { key: 'analytics_session_medium_table', label: 'Top-20 Session Medium Table', category: 'insights' },
  { key: 'analytics_traffic_countries_grid', label: 'Traffic Countries Grid', category: 'insights' },
  // SEO Report
  { key: 'seo_report_query_counting', label: 'Query Counting Card', category: 'metrics' },
  { key: 'seo_report_branded_vs_nonbranded', label: 'Branded vs Non-Branded', category: 'metrics' },
  { key: 'seo_report_gsc_monthly_chart', label: 'GSC Monthly Chart', category: 'insights' },
  { key: 'seo_report_top_pages', label: 'Top Pages Table', category: 'insights' },
  { key: 'seo_report_top_queries', label: 'Top Queries Table', category: 'insights' },
  { key: 'seo_report_countries', label: 'Countries Pie Chart', category: 'insights' },
  { key: 'seo_report_devices', label: 'Devices Donut Chart', category: 'insights' },
  { key: 'seo_report_declines', label: 'Decline Analysis', category: 'insights' },
  { key: 'seo_report_increases', label: 'Increase Analysis', category: 'insights' },
  // SEO Dashboard
  { key: 'seo_dashboard_overview_cards', label: 'SEO Overview Cards', category: 'metrics' },
  { key: 'seo_dashboard_stats_cards', label: 'SEO Stats Cards', category: 'metrics' },
  { key: 'seo_dashboard_keyword_sources', label: 'Keyword Sources Cards', category: 'insights' },
  { key: 'seo_dashboard_on_page_section', label: 'On-Page SEO Section', category: 'insights' },
  { key: 'seo_dashboard_top_countries', label: 'Top Countries Card', category: 'insights' },
  { key: 'seo_dashboard_keyword_opportunities', label: 'Keyword Opportunities Chart', category: 'insights' },
  { key: 'seo_dashboard_recent_activity', label: 'Recent Activity Card', category: 'activity' },
  // Scale 2Y3X Dashboard
  { key: 'scale_2y3x_team_avatars', label: 'Team Avatars', category: 'header' },
  { key: 'scale_2y3x_goals_card', label: 'Goals Card', category: 'metrics' },
  { key: 'scale_2y3x_growth_lab_team_card', label: 'Growth Lab Team Card', category: 'metrics' },
  { key: 'scale_2y3x_active_sprints_card', label: 'Active Sprints Card', category: 'metrics' },
  { key: 'scale_2y3x_ideas_card', label: 'Ideas Card', category: 'metrics' },
  { key: 'scale_2y3x_flagged_ideas', label: 'Flagged Ideas to Revisit', category: 'activity' },
  { key: 'scale_2y3x_time_progress', label: 'Time Progress', category: 'insights' },
  { key: 'scale_2y3x_qualitative_task_progress', label: 'Qualitative Task Progress', category: 'insights' },
  { key: 'scale_2y3x_goal_progress', label: 'Goal Progress', category: 'insights' },
  { key: 'scale_2y3x_team_members_list', label: 'Team Members List', category: 'activity' },
  // Sales Insights
  { key: 'sales_insights_total_proposals', label: 'Total Proposals', category: 'metrics' },
  { key: 'sales_insights_draft', label: 'Draft Proposals', category: 'metrics' },
  { key: 'sales_insights_responded', label: 'Responded Proposals', category: 'metrics' },
  { key: 'sales_insights_awarded', label: 'Awarded Proposals', category: 'metrics' },
  { key: 'sales_insights_total_value_awarded', label: 'Total Value Awarded', category: 'metrics' },
  { key: 'sales_insights_total_value', label: 'Total Value', category: 'metrics' },
  { key: 'sales_insights_avg_views_per_proposal', label: 'Avg Views per Proposal', category: 'metrics' },
  { key: 'sales_insights_avg_views_per_awarded', label: 'Avg Views per Awarded', category: 'metrics' },
  { key: 'sales_insights_awarded_vs_responded_chart', label: 'Awarded vs Responded Chart', category: 'insights' },
  { key: 'sales_insights_responded_vs_total_chart', label: 'Responded vs Total Chart', category: 'insights' },
  { key: 'sales_insights_proposals_table', label: 'Proposals Table', category: 'insights' },
  // Contacts
  { key: 'contacts_total_contacts', label: 'Total Contacts', category: 'metrics' },
  { key: 'contacts_total_organizations', label: 'Total Organizations', category: 'metrics' },
  { key: 'contacts_total_modules', label: 'Total Modules', category: 'metrics' },
  { key: 'contacts_total_activities', label: 'Total Activities', category: 'metrics' },
  { key: 'contacts_module_filters', label: 'Module Filters', category: 'header' },
  { key: 'contacts_activity_filters', label: 'Activity Filters', category: 'header' },
  { key: 'contacts_search', label: 'Search', category: 'header' },
  { key: 'contacts_table', label: 'Contacts Table', category: 'activity' },
  // User Activity Tracking
  { key: 'user_activity_filters', label: 'Brand / User / Date Filters', category: 'header' },
  { key: 'user_activity_total_time', label: 'Total Time', category: 'metrics' },
  { key: 'user_activity_total_visits', label: 'Total Visits', category: 'metrics' },
  { key: 'user_activity_unique_routes', label: 'Unique Routes', category: 'metrics' },
  { key: 'user_activity_unique_users', label: 'Unique Users', category: 'metrics' },
  { key: 'user_activity_over_time_chart', label: 'Activity Over Time', category: 'insights' },
  { key: 'user_activity_time_by_brand_chart', label: 'Time by Brand', category: 'insights' },
  { key: 'user_activity_records_table', label: 'Activity Records Table', category: 'activity' },
  // User Time Tracking
  { key: 'user_time_filters', label: 'Brand / User / Date / Entry Type Filters', category: 'header' },
  { key: 'user_time_total_minutes', label: 'Total Time', category: 'metrics' },
  { key: 'user_time_foundational', label: 'Foundational', category: 'metrics' },
  { key: 'user_time_growth', label: 'Growth', category: 'metrics' },
  { key: 'user_time_recurring', label: 'Recurring', category: 'metrics' },
  { key: 'user_time_logged_over_time_chart', label: 'Time Logged Over Time', category: 'insights' },
  { key: 'user_time_by_task_type_chart', label: 'By Task Type', category: 'insights' },
  { key: 'user_time_by_source_chart', label: 'By Source', category: 'insights' },
  { key: 'user_time_by_brand_chart', label: 'By Brand', category: 'insights' },
  { key: 'user_time_entries_table', label: 'Time Entries Table', category: 'activity' },
  // Growth Plan
  { key: 'growth_standup_day_start', label: 'Day Start', category: 'activity' },
  { key: 'growth_standup_day_end', label: 'Day End', category: 'activity' },
  { key: 'growth_standup_today_summary', label: "Today's Standup Summary", category: 'metrics' },
  { key: 'growth_standup_day_start_only', label: 'Day Start (separate)', category: 'activity' },
  { key: 'growth_standup_day_end_only', label: 'Day End (separate)', category: 'activity' },
  // User Settings
  { key: 'user_settings_favorites', label: 'User Settings Favorites', category: 'activity' },
  { key: 'user_settings_languages', label: 'User Settings Languages', category: 'activity' },
  { key: 'user_settings_themes', label: 'User Settings Themes', category: 'activity' },
  // Sales Insights (extra metrics when added via settings)
  { key: 'sales_insights_weighted_avg_deal', label: 'Weighted Avg Deal', category: 'metrics' },
  { key: 'sales_insights_avg_deal_value_awarded', label: 'Avg Deal Value Awarded', category: 'metrics' },
  { key: 'sales_insights_avg_sales_cycle', label: 'Avg Sales Cycle', category: 'metrics' },
];

/** Re-export registered routes for UI hints. */
export { REGISTERED_ROUTES } from './routeComponentsRegistry';

/** Get components for a route from the registry. Returns actual components ON that page. No pattern matching. */
export function getComponentsForRoute(route: string): Array<{
  key: string;
  label: string;
  category: string;
}> {
  return getComponentsForRouteFromRegistry(route);
}
